import {
  type EmployeeHistory,
  type EmployeeHistoryParams,
  getEmployeeHistory,
} from '@kizen/api/employee';
import type { DefaultError, QueryObserverOptions } from '@tanstack/query-core';
import type { AxiosInstance } from 'axios';
import type { Drop, Resource, WithParams, WithRequired } from './types';
import { curry1, getResource } from './utils';

type EmployeeHistoryQuery<
  TData = EmployeeHistory,
  TError = DefaultError,
> = WithRequired<
  QueryObserverOptions<
    EmployeeHistory,
    TError,
    TData,
    EmployeeHistory,
    [Resource<Readonly<['employee', string, 'history']>, EmployeeHistoryParams>]
  >,
  'queryFn' | 'queryKey'
>;

export const employeeHistoryQuery = <TData = EmployeeHistory>(
  axios: AxiosInstance,
  id: string,
  options: WithParams<
    Omit<EmployeeHistoryQuery<TData>, 'queryFn' | 'queryKey'>,
    EmployeeHistoryParams
  > = {}
): EmployeeHistoryQuery<TData> => {
  const path = ['employee', id, 'history'] as const;
  const { params, ...queryOpts } = options;
  const resource = getResource(path, { params });

  return <EmployeeHistoryQuery<TData>>{
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return getEmployeeHistory(axios, qk.path[1], {
        params: qk.params,
        signal,
      });
    },
    ...queryOpts,
  };
};

class Queries {
  public employeeHistoryQuery: <TData = EmployeeHistory>(
    ...args: Drop<Parameters<typeof employeeHistoryQuery<TData>>>
  ) => EmployeeHistoryQuery<TData>;

  constructor(axios: AxiosInstance) {
    this.employeeHistoryQuery = curry1(employeeHistoryQuery)(axios);
  }
}

export type EmployeeQueries = Queries;

export const defEmployeeQueries = (axios: AxiosInstance): EmployeeQueries => {
  return new Queries(axios);
};
